import { Button, Caption1, Dropdown, Input, Option, makeStyles, tokens } from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';

import { CHANGE_MANAGER_CANDIDATES } from '../../data';
import { CHANGE_STATUS_OPTIONS, CRITICALITY_OPTIONS } from '../../constants';
import { TDashboardFilters, hasActiveFilters } from '../../utils';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  label: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  // The search flexes to fill the remaining width; the dropdowns keep a fixed
  // size and never shrink, so the whole row stays on a single line.
  search: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: '160px',
  },
  dropdown: {
    width: '170px',
    flexShrink: 0,
  },
  clear: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
});

type TFilterDropdownProps = {
  emptyLabel: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  className?: string;
};

const FilterDropdown = ({ emptyLabel, value, options, onChange, className }: TFilterDropdownProps) => (
  <Dropdown
    aria-label={emptyLabel}
    className={className}
    selectedOptions={[value]}
    value={value || emptyLabel}
    onOptionSelect={(_, data) => onChange((data.optionValue as string) ?? '')}
  >
    <Option value="" text={emptyLabel}>
      {emptyLabel}
    </Option>
    {options.map((option) => (
      <Option key={option} value={option} text={option}>
        {option}
      </Option>
    ))}
  </Dropdown>
);

type TProps = {
  filters: TDashboardFilters;
  setFilters: (updater: (current: TDashboardFilters) => TDashboardFilters) => void;
  onClear: () => void;
};

export const ChangeFilters = ({ filters, setFilters, onClear }: TProps) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Caption1 className={classes.label}>Changes</Caption1>
      <Input
        className={classes.search}
        contentBefore={<SearchRegular />}
        placeholder="Search by ID or title"
        value={filters.search}
        onChange={(_, data) => setFilters((current) => ({ ...current, search: data.value }))}
      />
      <FilterDropdown
        className={classes.dropdown}
        emptyLabel="All statuses"
        value={filters.status}
        options={CHANGE_STATUS_OPTIONS}
        onChange={(value) => setFilters((current) => ({ ...current, status: value as TDashboardFilters['status'] }))}
      />
      <FilterDropdown
        className={classes.dropdown}
        emptyLabel="All change managers"
        value={filters.changeManager}
        options={CHANGE_MANAGER_CANDIDATES}
        onChange={(value) => setFilters((current) => ({ ...current, changeManager: value }))}
      />
      <FilterDropdown
        className={classes.dropdown}
        emptyLabel="All criticalities"
        value={filters.criticality}
        options={CRITICALITY_OPTIONS}
        onChange={(value) =>
          setFilters((current) => ({ ...current, criticality: value as TDashboardFilters['criticality'] }))
        }
      />
      <Button
        appearance="subtle"
        icon={<DismissRegular />}
        className={classes.clear}
        disabled={!hasActiveFilters(filters)}
        onClick={onClear}
      >
        Clear filters
      </Button>
    </div>
  );
};
